import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * The dialog width scale. Exported so tests and the style guide enumerate the
 * same map the component uses, rather than a copy that can drift.
 */
export const DIALOG_SIZES = {
  sm: 'max-w-[480px]',
  md: 'max-w-[640px]',
  lg: 'max-w-[800px]',
  xl: 'max-w-[1152px] h-[min(88vh,900px)]',
  full: 'h-[calc(100dvh-32px)] w-[calc(100vw-32px)] max-w-none',
}

/**
 * Shared by Dialog and AlertDialog. Below `sm:` every size is clamped to the
 * viewport, so a phone never gets a dialog wider than its screen.
 */
export const DIALOG_CONTENT_BASE =
  'fixed left-1/2 top-1/2 z-50 flex w-full max-h-[min(88dvh,900px)] -translate-x-1/2 -translate-y-1/2 ' +
  'flex-col gap-4 overflow-x-hidden overflow-y-auto overscroll-contain rounded-surface border border-line bg-surface p-6 text-ink ' +
  'shadow-overlay animate-pop-in focus:outline-none max-sm:max-w-[calc(100vw-32px)]'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-scrim animate-fade-in', className)}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * @param {Object} props
 * @param {'sm'|'md'|'lg'|'xl'|'full'} [props.size='md'] - Width from DIALOG_SIZES
 * @param {boolean} [props.hideClose=false] - Drop the corner close button
 * @param {string} [props.className] - Merged last, so an un-swept
 *   `className="max-w-2xl"` still wins until the dialog-size sweep lands
 */
const DialogContent = React.forwardRef(
  ({ className, children, size = 'md', hideClose = false, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        className={cn(DIALOG_CONTENT_BASE, DIALOG_SIZES[size] ?? DIALOG_SIZES.md, className)}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close className="absolute right-2 top-2 flex size-11 items-center justify-center rounded-control text-ink-2 hover:bg-accent hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface">
            <X className="size-4" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }) => (
  <div className={cn('flex flex-col gap-1.5 pr-8 text-left', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

/**
 * The scrolling middle of a dialog. The negative margin keeps the scrollbar at
 * the dialog edge while the content keeps the 24px gutter, and the padding
 * stays on DialogContent so un-swept dialogs that place raw children still
 * render correctly. DialogContent itself scrolls as a fallback, so a dialog
 * without a DialogBody is never clipped.
 */
const DialogBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6', className)}
    {...props}
  />
))
DialogBody.displayName = 'DialogBody'

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 border-t border-line pt-4 sm:flex-row sm:justify-end',
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('text-title text-ink [overflow-wrap:anywhere]', className)} {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-body text-ink-2', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
